import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { alertOnFailure } from "@redotech/redo-web/alert";
import {
  ProductState,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";

export function useProductAutocomplete(
  teamId: string,
  currentVariantId?: string,
) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [productsSearchString, setProductsSearchString] = useState("");
  const [productOptions, setProductOptions] = useState<ProductVariantInfo[]>(
    [],
  );

  const [selectedProduct, setSelectedProduct] =
    useState<ProductVariantInfo | null>(null);

  const [productOptionsLoad, doProductOptionsLoad] = useTriggerLoad(
    async (signal) => {
      try {
        const results = await rpcClient.searchProductVariants(
          { query: productsSearchString, teamId },
          { signal },
        );
        setProductOptions(results || []);
        return results || [];
      } catch (error) {
        if (
          error instanceof Error &&
          error.message.includes("Failed to search product variants - Error: ")
        ) {
          void alertOnFailure("Error searching products")(() => {
            throw error;
          });
        }
        setProductOptions([]);
        return [];
      }
    },
  );

  const debouncedProductsSearchString = useDebounce(productsSearchString, 500);

  useEffect(() => {
    if (!selectedProduct && currentVariantId) {
      void (async () => {
        const productDetails = await rpcClient.getVariantById({
          variantId: currentVariantId,
          teamId,
        });
        if (productDetails) {
          setSelectedProduct(productDetails);
        } else {
          setSelectedProduct({
            variantId: currentVariantId,
            name: "Selected Product",
            imageSrc: "",
          });
        }
      })();
    }
    // This needs to run only once when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load products when the search string changes
  useEffect(() => {
    void doProductOptionsLoad();
  }, [debouncedProductsSearchString, doProductOptionsLoad]);

  const onProductsInputChange = (_event: any, value: string) => {
    setProductsSearchString(value);
  };

  const productsState: ProductState = {
    loading: productOptionsLoad.pending,
    options: productOptions,
    onProductsInputChange,
  };

  return {
    productsState,
    selectedProduct,
    onSelectedProductChange: setSelectedProduct,
  };
}
